
import { EmailDomain } from "@/types/EmailDomain";
import {
  createEmailDomain,
  getEmailDomainById,
  updateEmailDomain,
  deleteEmailDomainInEvent,
  deleteEmailDomainInCompany,
  getEmailDomainsByEvent,
  getEmailDomainsByCompany,
  createSenderAddressIdentity,
  confirmSenderAddressOtp,
  resendSenderAddressVerification,
  type ConfirmSenderAddressOtpResult,
  type ResendSenderAddressVerificationResult,
} from "@/lib/api/clients/EmailDomainClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";
import { getCookie } from "@/lib/auth/cookies";

export async function createEmailDomainHandler(
  domain: string,
  company: boolean,
  eventId?: string
): Promise<EmailDomain | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    // Validate domain name
    if (!domain || domain.trim().length === 0) {
      throw new Error("validationError");
    }

    if (company && !companyId) {
      throw new Error("validationError");
    }

    if (!company && !eventId) {
      throw new Error("validationError");
    }

    // Validate companyId if provided
    if (companyId && !isValidObjectId(companyId)) {
      throw new Error("validationError");
    }

    // Validate eventId if provided
    if (eventId && !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }
    return await createEmailDomain(
      domain,
      company,
      companyId as string,
      eventId
    );
  } catch (error) {
    console.error("Error creating email domain:", error);
    return handleErrorServer(error);
  }
}

export async function getEmailDomainByIdHandler(
  emailDomainId: string,
  fields: string = "emailDomainId,eventId,companyId,domain,dnsAuthValidation,dnsEntryDKIM1,dnsEntryDKIM2,dnsEntryDKIM3,dnsCustom,dnsStateAuthValidation,dnsStateDKIM,dnsStateCustom,emailDomainState,ownershipProofRequired,identityType,localPart,dmarcPolicy,failureReason,sesVerifyVia,sesLinkSentAt,proofVerifiedAt,otpRequestedBy,otpRequestedAt,dkimCovered,sesCheckedAt,sesCheckOutcome"
): Promise<EmailDomain | ServerError> {
  try {
    // Validate emailDomainId
    if (!isValidObjectId(emailDomainId)) {
      throw new Error("validationError");
    }

    // Validate fields
    if (!fields || fields.trim().length === 0) {
      throw new Error("validationError");
    }

    return await getEmailDomainById(emailDomainId, fields);
  } catch (error) {
    // A 403 here is EXPECTED, not an anomaly: `GET /email-domains/{id}` is
    // `@RolesAllowed({"OWNER"})` while the by-event listing this complements
    // admits nine roles, so every ADMIN/EDITOR/VIEWER and every EVENT-*
    // collaborator is refused by design. `topUpMissingDomains` already stops at
    // the first refusal; logging it at error level only trained people to
    // ignore a red line in the send dialog's console. Everything else stays
    // loud.
    if (isForbidden(error)) {
      console.warn(
        "email domain by-id refused (OWNER-only lane; expected for this role):",
        emailDomainId,
      );
    } else {
      console.error("Error getting email domain by ID:", error);
    }
    return handleErrorServer(error);
  }
}

/** True for a 403 from either shape the clients throw (axios error or the
 *  shim's rethrown `Error` carrying the backend code). */
function isForbidden(error: unknown): boolean {
  const status = (error as { response?: { status?: number }; status?: number } | null)
    ?.response?.status ?? (error as { status?: number } | null)?.status;
  if (status === 403) return true;
  const message = error instanceof Error ? error.message : "";
  return message === "noAccessToResource" || message === "insufficientCapability";
}

export async function updateEmailDomainHandler(
  emailDomainId: string,
  eventId?: string,
  companyId?: string,
  domain?: string,
  dnsAuthValidation?: string,
  dnsEntryDKIM1?: string,
  dnsEntryDKIM2?: string,
  dnsEntryDKIM3?: string,
  dnsCustom?: string,
  dnsStateAuthValidation?: string,
  dnsStateDKIM?: string,
  dnsStateCustom?: string,
  emailDomainState?: string
): Promise<void | ServerError> {
  try {
    // Validate emailDomainId
    if (!isValidObjectId(emailDomainId)) {
      throw new Error("validationError");
    }

    // Validate eventId if provided
    if (eventId && !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    // Validate companyId if provided
    if (companyId && !isValidObjectId(companyId)) {
      throw new Error("validationError");
    }

    // Validate domain name if provided
    if (domain && domain.trim().length === 0) {
      throw new Error("validationError");
    }

    // Validate DNS states if provided
    const validStates = ["PENDING", "VERIFIED", "FAILED"];
    if (dnsStateAuthValidation && !validStates.includes(dnsStateAuthValidation)) {
      throw new Error("validationError");
    }
    if (dnsStateDKIM && !validStates.includes(dnsStateDKIM)) {
      throw new Error("validationError");
    }
    if (dnsStateCustom && !validStates.includes(dnsStateCustom)) {
      throw new Error("validationError");
    }
    if (emailDomainState && !validStates.includes(emailDomainState)) {
      throw new Error("validationError");
    }

    await updateEmailDomain(
      emailDomainId,
      eventId,
      companyId,
      domain,
      dnsAuthValidation,
      dnsEntryDKIM1,
      dnsEntryDKIM2,
      dnsEntryDKIM3,
      dnsCustom,
      dnsStateAuthValidation,
      dnsStateDKIM,
      dnsStateCustom,
      emailDomainState
    );
  } catch (error) {
    console.error("Error updating email domain:", error);
    return handleErrorServer(error);
  }
}

export async function deleteEmailDomainInEventHandler(
  emailDomainId: string,
  eventId: string
): Promise<boolean | ServerError> {
  try {
    // Validate emailDomainId
    if (!isValidObjectId(emailDomainId)) {
      throw new Error("validationError");
    }

    // Validate eventId
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    return await deleteEmailDomainInEvent(emailDomainId, eventId);
  } catch (error) {
    console.error("Error deleting email domain in event:", error);
    return handleErrorServer(error);
  }
}

export async function deleteEmailDomainInCompanyHandler(
  emailDomainId: string,
  companyId: string
): Promise<boolean | ServerError> {
  try {
    // Validate emailDomainId
    if (!isValidObjectId(emailDomainId)) {
      throw new Error("validationError");
    }

    // Validate companyId
    if (!isValidObjectId(companyId)) {
      throw new Error("validationError");
    }

    return await deleteEmailDomainInCompany(emailDomainId, companyId);
  } catch (error) {
    console.error("Error deleting email domain in company:", error);
    return handleErrorServer(error);
  }
}

export async function getEmailDomainsByEventHandler(
  eventId: string,
  page: number = 0,
  limit: number = 10,
  fields: string = "emailDomainId,eventId,companyId,domain,dnsAuthValidation,dnsEntryDKIM1,dnsEntryDKIM2,dnsEntryDKIM3,dnsCustom,dnsStateAuthValidation,dnsStateDKIM,dnsStateCustom,emailDomainState,ownershipProofRequired,identityType,localPart,dmarcPolicy,failureReason,sesVerifyVia,sesLinkSentAt,proofVerifiedAt,otpRequestedAt,dkimCovered,sesCheckedAt,sesCheckOutcome"
): Promise<EmailDomain[] | ServerError> {
  try {
    // Validate eventId
    if (!isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    // Validate fields
    if (!fields || fields.trim().length === 0) {
      throw new Error("validationError");
    }

    // Validate pagination parameters
    if (typeof page !== "number" || page < 0) {
      throw new Error("validationError");
    }
    if (typeof limit !== "number" || limit <= 0 || limit > 100) {
      throw new Error("validationError");
    }

    return await getEmailDomainsByEvent(
      eventId,
      page,
      limit,
      fields
    );
  } catch (error) {
    console.error("Error getting email domains by event:", error);
    return handleErrorServer(error);
  }
}

export async function getEmailDomainsByCompanyHandler(
  companyId: string,
  page: number = 0,
  limit: number = 10,
  fields: string = "emailDomainId,eventId,companyId,domain,dnsAuthValidation,dnsEntryDKIM1,dnsEntryDKIM2,dnsEntryDKIM3,dnsCustom,dnsStateAuthValidation,dnsStateDKIM,dnsStateCustom,emailDomainState,ownershipProofRequired,identityType,localPart,dmarcPolicy,failureReason,sesVerifyVia,sesLinkSentAt,proofVerifiedAt,otpRequestedAt,dkimCovered,sesCheckedAt,sesCheckOutcome"
): Promise<EmailDomain[] | ServerError> {
  try {
    // Validate companyId
    if (!isValidObjectId(companyId)) {
      throw new Error("validationError");
    }

    // Validate fields
    if (!fields || fields.trim().length === 0) {
      throw new Error("validationError");
    }

    // Validate pagination parameters
    if (typeof page !== "number" || page < 0) {
      throw new Error("validationError");
    }
    if (typeof limit !== "number" || limit <= 0 || limit > 100) {
      throw new Error("validationError");
    }

    return await getEmailDomainsByCompany(
      companyId,
      page,
      limit,
      fields
    );
  } catch (error) {
    console.error("Error getting email domains by company:", error);
    return handleErrorServer(error);
  }
}


/**
 * Mirror of the backend's `CreateAddressIdentity` pattern (case-insensitive):
 * local part `[A-Za-z0-9._%+-]{1,64}` @ hostname, at most 254 characters.
 * The local-part alphabet has no whitespace, so CR/LF can never reach a header.
 */
const SENDER_ADDRESS_PATTERN =
  /^[a-z0-9._%+-]{1,64}@(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

/** Lowercased + trimmed, as the backend stores it (SES identities are case-sensitive). */
export function normalizeSenderAddress(address: string): string {
  return address.trim().toLowerCase();
}

export function isValidSenderAddress(address: string): boolean {
  const normalized = normalizeSenderAddress(address);
  return normalized.length <= 254 && SENDER_ADDRESS_PATTERN.test(normalized);
}

export async function createSenderAddressIdentityHandler(
  address: string,
  company: boolean,
  eventId?: string
): Promise<EmailDomain | ServerError> {
  try {
    const normalized = normalizeSenderAddress(address ?? "");
    if (!isValidSenderAddress(normalized)) {
      throw new Error("invalidSenderAddress");
    }
    if (!company && (!eventId || !isValidObjectId(eventId))) {
      throw new Error("validationError");
    }
    if (company) {
      const companyId = await getCookie("companyId");
      if (!companyId || !isValidObjectId(companyId)) {
        throw new Error("validationError");
      }
    }
    return await createSenderAddressIdentity(
      normalized,
      company,
      company ? undefined : eventId
    );
  } catch (error) {
    // No address in the log line: it is personal data.
    console.error("Error creating sender address identity");
    return handleErrorServer(error);
  }
}

export async function confirmSenderAddressOtpHandler(
  emailDomainId: string,
  code: string
): Promise<ConfirmSenderAddressOtpResult | ServerError> {
  try {
    if (!isValidObjectId(emailDomainId)) {
      throw new Error("validationError");
    }
    if (!/^[0-9]{6}$/.test(code ?? "")) {
      throw new Error("invalidOtp");
    }
    return await confirmSenderAddressOtp(emailDomainId, code);
  } catch (error) {
    console.error("Error confirming sender address code");
    return handleErrorServer(error);
  }
}

export async function resendSenderAddressVerificationHandler(
  emailDomainId: string
): Promise<ResendSenderAddressVerificationResult | ServerError> {
  try {
    if (!isValidObjectId(emailDomainId)) {
      throw new Error("validationError");
    }
    return await resendSenderAddressVerification(emailDomainId);
  } catch (error) {
    console.error("Error re-sending sender address verification");
    return handleErrorServer(error);
  }
}
